/**
 * 알림톡 템플릿 관리 API — 알리고 콘솔 프록시.
 *
 * BE 는 우리 DB 에 저장하지 않고 알리고 API 로 forward 만 함.
 * 응답은 알리고 원본 필드명 그대로 (templtCode/templtName/templtContent/...).
 */
import { apiClient } from './client';

function unwrap<T>(res: any, fallback?: T): T {
  if (res && typeof res === 'object' && 'data' in res) {
    const d = (res as any).data;
    return (d ?? fallback ?? d) as T;
  }
  return (res ?? fallback) as T;
}

/** 알리고 템플릿 — list/get 응답 1건. */
export interface AligoTemplate {
  templtCode: string;       // 알리고 자동 발급 (TF_xxxx 또는 STUB_x)
  templtName: string;       // 우리 식별 코드 (운영자 약속, 예: USER_RESERVE_APPROVED)
  templtContent: string;    // 본문 (변수 #{var})
  senderKey?: string;
  templateType?: string;    // BA/EX/AD/MI
  templateEmType?: string;  // NONE/TEXT/IMAGE
  templtTitle?: string;
  templtSubtitle?: string;
  templtImageUrl?: string;
  status?: string;          // S=중단, A=정상, R=대기
  inspStatus?: string;      // REG=등록, REQ=심사요청, APR=승인, REJ=반려
  cdate?: string;
  buttons?: AligoButton[];
  comments?: AligoComment[];
}

/** 알리고 코멘트 — inspStatus=REJ 시 반려 사유. */
export interface AligoComment {
  /** 사유/코멘트 본문. */
  content?: string;
  /** 작성자 (심의팀 등). */
  userName?: string;
  /** 등록 일시. */
  regDate?: string;
  /** 상태 코드. */
  status?: string;
}

export interface AligoButton {
  ordering?: string;
  name?: string;
  linkType?: string;        // AC/DS/WL/AL/BK/MD
  linkTypeName?: string;
  linkMo?: string;
  linkPc?: string;
  linkIos?: string;
  linkAnd?: string;
}

/** 등록/수정 요청 본문. */
export interface AligoTemplateUpsertReq {
  templateName: string;     // 필수 — 우리 식별 코드
  templateContent: string;
  templateType?: string;
  emphasisType?: string;
  emphasisTitle?: string;
  emphasisSubtitle?: string;
  advertText?: string;
  extraText?: string;
  secureYn?: 'Y' | 'N';
  buttonsJson?: string;     // '{"button":[...]}' 전체 문자열
}

const BASE = '/admin/kakao-alim-templates';

export const kakaoAlimTemplateApi = {
  /** 목록 — refresh=true 면 BE 캐시 무효화. */
  async list(refresh = false): Promise<AligoTemplate[]> {
    const url = refresh ? `${BASE}?refresh=true` : BASE;
    return unwrap<AligoTemplate[]>(await apiClient.get(url), []);
  },
  async getOne(aligoCode: string): Promise<AligoTemplate> {
    return unwrap<AligoTemplate>(await apiClient.get(`${BASE}/${encodeURIComponent(aligoCode)}`));
  },
  async create(req: AligoTemplateUpsertReq): Promise<AligoTemplate> {
    return unwrap<AligoTemplate>(await apiClient.post(BASE, req));
  },
  async update(aligoCode: string, req: AligoTemplateUpsertReq): Promise<AligoTemplate> {
    return unwrap<AligoTemplate>(
      await apiClient.patch(`${BASE}/${encodeURIComponent(aligoCode)}`, req));
  },
  /** 수정 + 즉시 재검수 요청 (반려 → 수정 후 재검수 통합 처리, 서버측 지연 완충 포함). */
  async updateAndRequestReview(
    aligoCode: string, req: AligoTemplateUpsertReq
  ): Promise<AligoTemplate> {
    return unwrap<AligoTemplate>(
      await apiClient.patch(
        `${BASE}/${encodeURIComponent(aligoCode)}?autoReview=true`, req));
  },
  async requestReview(aligoCode: string): Promise<void> {
    await apiClient.post(`${BASE}/${encodeURIComponent(aligoCode)}/review`, {});
  },
  /** 이름만 변경 (리스트 인라인 편집). REG/REJ 상태만 가능. */
  async rename(aligoCode: string, templateName: string): Promise<AligoTemplate> {
    return unwrap<AligoTemplate>(
      await apiClient.patch(`${BASE}/${encodeURIComponent(aligoCode)}/name`, { templateName })
    );
  },
  async delete(aligoCode: string): Promise<void> {
    await apiClient.delete(`${BASE}/${encodeURIComponent(aligoCode)}`);
  },
};

/** 검수 상태 라벨. */
export const INSP_STATUS_LABEL: Record<string, string> = {
  REG: '등록',
  REQ: '심사요청',
  APR: '승인',
  REJ: '반려',
};

/** 운영 상태 라벨. */
export const STATUS_LABEL: Record<string, string> = {
  S: '중단',
  A: '정상',
  R: '대기',
};
